import '../styles/dimensional-container-info.css';

import {
  faArrowUpShortWide,
  faBoxes,
  faBoxOpen,
  faCube,
  faInfoCircle,
  faLifeRing,
  faLocationDot,
  faObjectGroup,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import Alert from 'src/components/ui/Alert';
import InfoRow from 'src/components/ui/InfoRow';
import ResourceCardAlt from 'src/components/ui/ResourceCardAlt';
import ResourceTitleCard from 'src/components/ui/ResourceTitleCard';
import { useLocation, useSubLocation } from 'src/hooks/services/useLocation';
import { useMaterial } from 'src/hooks/services/useMaterials';
import { MATERIAL_TYPE_BADGE_MAP, MATERIAL_TYPES_MAP } from 'src/utils/constants';
import { formatUnits } from 'src/utils/conversions';
import { getShortUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

const DimensionalContainerInfo = ({ container, uuid, user }) => {
  const { data: material, isLoading: materialLoading } = useMaterial(
    container?.material,
    'dimensional-material'
  );

  const { data: location, isLoading: locationLoading } = useLocation(
    container?.location,
    'dimensional-location'
  );
  const { data: subLocation, isLoading: subLocationLoading } = useSubLocation(
    container?.sub_location,
    'dimensional-sub-location'
  );

  const isLoading = materialLoading || locationLoading || subLocationLoading;

  if (isLoading) {
    return (
      <>
        <Header excludeFromHistory title='Container Information' user={user} />
        <main role='main' className='text-center'>
          <Loader inline text='container details' />
        </main>
      </>
    );
  }

  const getMaterialTypeIcon = materialTypeName => {
    const iconMap = {
      Base: faCube,
      Support: faLifeRing,
      Consumable: faBoxOpen,
      'Raw Material': faBoxes,
    };
    return iconMap[materialTypeName] || faTag;
  };

  const getMaterialTypeColor = materialTypeName => {
    const badgeConfig = MATERIAL_TYPE_BADGE_MAP[materialTypeName];
    return badgeConfig?.color || '#6b7280';
  };

  return (
    <>
      <Header title='Container Information' user={user} />
      <main role='main' className='dimensional-container-info'>
        <Alert variant='info' icon={faInfoCircle} title='Information'>
          Consumption must be recorded through the Run Record Page. Please navigate to your active
          run to consume this material.
        </Alert>

        <ResourceTitleCard
          icon={faBoxOpen}
          title='Material Container'
          subtitle={getShortUuid(uuid)}
        />

        <ResourceCardAlt title='Container Details'>
          <InfoRow
            icon={faBoxOpen}
            label='Type'
            value={container?.disposable ? 'Disposable' : 'Permanent'}
          />

          <InfoRow
            icon={faArrowUpShortWide}
            label='Quantity'
            value={
              container?.quantity && material?.units ? (
                <span>
                  {container.quantity} {formatUnits(material.units)}
                </span>
              ) : (
                'N/A'
              )
            }
          />
        </ResourceCardAlt>

        <ResourceCardAlt title='Stock Details'>
          <InfoRow icon={faTag} label='Name' value={material?.name || 'N/A'} />

          <InfoRow
            icon={faObjectGroup}
            label='Type'
            value={
              material?.type ? (
                <span
                  className='material-type-badge'
                  style={{
                    backgroundColor: getMaterialTypeColor(MATERIAL_TYPES_MAP[material.type]),
                  }}
                >
                  <FontAwesomeIcon
                    icon={getMaterialTypeIcon(MATERIAL_TYPES_MAP[material.type])}
                    className='material-type-icon'
                  />
                  {MATERIAL_TYPES_MAP[material.type]}
                </span>
              ) : (
                'N/A'
              )
            }
          />

          {(location || subLocation) && (
            <InfoRow
              icon={faLocationDot}
              label='Location'
              value={
                <div className='dimensional-info-location'>
                  <div className='dimensional-info-location-primary'>{location?.name || 'N/A'}</div>
                  {subLocation?.name && (
                    <div className='dimensional-info-location-secondary'>{subLocation.name}</div>
                  )}
                </div>
              }
            />
          )}
        </ResourceCardAlt>
      </main>
    </>
  );
};

DimensionalContainerInfo.propTypes = {
  container: PropTypes.object.isRequired,
  uuid: PropTypes.string.isRequired,
  user: userPropType,
};

DimensionalContainerInfo.defaultProps = {
  user: null,
};

export default DimensionalContainerInfo;
